import { Box, Stack } from "@mui/material";
import React, { forwardRef, Ref, useEffect, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useAcknowledgeAlarm } from "../../queries/alarm-setpoints/UseAcknowledgeAlarm";
import { useAlarmSetpoints } from "../../queries/alarm-setpoints/UseAlarmSetpoints";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { ISetPointViewModel } from "../../services/alarm-setpoints-data-service/Models";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import AlarmSetpointDisableDialog, { bindAlarmSetpointDisableDialog, useAlarmSetpointDisableDialogState } from "../alarm-setpoint-disable-dialog/AlarmSetpointDisableDialog";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import GroupedVirtualizedList, { GroupedVirtualizedListItem } from "../grouped-virtualized-list/GroupedVirtualizedList";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSwitch from "../side-sheet/side-sheet-switch/SideSheetSwitch";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import { AlarmSetpointListItem } from "./AlarmSetpointListItem";
import useAlarmSetpointListState from "./AlarmSetpointListState";
import AlarmSetpointEditorDialog, { bindAlarmSetpointEditorDialog, useAlarmSetpointEditorDialogState } from "../alarm-setpoint-editor/AlarmSetpointEditorDialog";
import { useGroup } from "../../queries/groups/UseGroup";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ITagViewState } from "../../services/state-data-service/Models";
import { useUnlinkTagSetpoint } from "../../queries/alarm-setpoints/UseUnlinkTagSetpoint";
import { RestError } from "../../services/rest-service/RestService";
import SetpointAdministrationDataService from "../../services/setpoint-administration-service/SetpointAdministrationService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import LinkExistingSetpointDialog, { useLinkExistingSetpointDialogState, bindLinkExistingSetpointDialog } from "../link-existing-setpoint-dialog/LinkExistingSetpointDialog";
import { useEnableObjectSetpoints } from "../../queries/alarm-setpoints/UseEnableObjectSetpoints";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../rename-configuration-dialog/RenameConfigurationDialog";
import { useSaveSetpoint } from "../../queries/alarm-setpoint-admin/UseSaveSetpoint";

export type AlarmSetpoint = ISetPointViewModel & GroupedVirtualizedListItem;

export type AlarmSetpointListProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type AlarmSetpointListType = {
    executeAction: (action: BaseActionEnum) => void;
};

enum AlarmState {
    InAlarm = 0,
    Acknowledged = 1,
    Disabled = 2,
    NotInAlarm = 3
}

function AlarmSetpointList({
    visible,
    setViewOptionState
}: AlarmSetpointListProps, ref: Ref<unknown> | undefined) {
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [, setAppNotificationState] = useAppNotificationContext();
    const disableDialog = useAlarmSetpointDisableDialogState();
    const { t } = useTranslation();
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const setpointEditorDialog = useAlarmSetpointEditorDialogState();
    const linkExistingSetpointDialog = useLinkExistingSetpointDialogState();
    const renameConfigurationDialog = useRenameConfigurationDialogState();

    const {
        state,
        resetOptions,
        setFilterText,
        setGroupByAlarmState
    } = useAlarmSetpointListState();

    useImperativeHandle<unknown, AlarmSetpointListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const { data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });

    const objectName = objectViewState?.contextObject.objectName ?? "";

    const { data, status, refetch } = useQueryNotifications(useAlarmSetpoints)({
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        options: {
            filterText: state.filterText,
            orderByAlarmState: state.groupByAlarmState
        },
        enabled: visible
    });

    const { mutate: acknowledgeAlarm } = useQueryNotifications(useAcknowledgeAlarm)({
        groupID: routeContext.groupID as number,
        onSuccess: () => {
            acknowledge(t("alarmSetpointList.acknowledgeNotificationText"));
        }
    });


    const { mutate: unlinkTag } = useQueryNotifications(useUnlinkTagSetpoint)({
        groupID: routeContext.groupID as number,
        onSuccess: async (data) => {
            acknowledge(t("setpointAdmin.successUnlinked"));

            if (data.count === 0) {

                const result = await confirm({
                    dialogTitle: t("setpointAdmin.noTagsRemaining"),
                    confirmationText: t("setpointAdmin.setpointDeleteConfirmation"),
                    yesButtonText: t("buttons.yes"),
                    noButtonText: t("buttons.no"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result === "yes") {
                    try {
                        await SetpointAdministrationDataService.deleteSetpoint(routeContext.groupID as number, data.setpointID);
                        acknowledge(t("setpointAdmin.successMessageDeleted"));
                    } catch (error) {
                        const restError = error as RestError;

                        setAppNotificationState({
                            notificationType: AppNotificationTypeEnum.Error,
                            show: true,
                            error: restError
                        });
                    }
                }
            }
        }
    });

    const { mutateAsync: enableObjectSetPoints } = useQueryNotifications(useEnableObjectSetpoints)({
        groupID: routeContext.groupID as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        objectID: routeContext.contextObject?.id as number,
        onSuccess: () => {
            acknowledge(t("alarmSetpointList.enableAllAlarmsAcknowledge"));
        }
    });

    const { mutate: saveSetpoint } = useQueryNotifications(useSaveSetpoint)({
        groupID: routeContext.groupID as number,
        organizationID: group?.organizationID as number,
        onSuccess: async (data) => {
            acknowledge(t("setpointAdmin.successMessageUpdated", { 
                                name: data.setPointName 
                        }));

            refetch();
        }
    });


    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState && setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ]);

    const orderedData = data?.alarmSetpoints ?? new Array<AlarmSetpoint>();

    function executeAction(action: BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                refetch();
                break;

            case BaseActionEnum.AddSetpoint:
                onAddSetpoint();
                break;

            case BaseActionEnum.LinkToSetpoint:
                onLinkSetpoint();
                break;

            case BaseActionEnum.DisableAllAlarms:
                onDisableAll();
                break;

            case BaseActionEnum.EnableAllAlarms:
                onEnableAll();
                break;
        }
    }

    async function _onEdit(setpointID: number, tagName: string) {

        let parentName = objectName;

        if (routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag) {
            const tagViewState = objectViewState as ITagViewState;
            parentName = tagViewState.tagObjectName;
        }

        const result = await setpointEditorDialog.open({
            groupID: routeContext.groupID as number,
            setpointID: setpointID,
            organizationID: group?.organizationID as number,
            objectName: parentName,
            tagName: tagName,
            tagID: routeContext?.tagID
        });

        if (result.reason === 'save') {

            refetch();
        }
    }

    async function onAddSetpoint() {
        return _onEdit(0, objectName);
    }


    async function onLinkSetpoint() {

        const linkedSetpoints = data?.alarmSetpoints.map(o => o.id) ?? [];

        const result = await linkExistingSetpointDialog.open({
            linkedSetpoints
        });

        if (result.reason === 'save') {

            refetch();
        }
    }

    async function onAcknowledge(setpoint: AlarmSetpoint): Promise<void> {
        acknowledgeAlarm(setpoint);
    };

    async function onDisable(setpoint: AlarmSetpoint): Promise<void> {
        await disableDialog.open({
            tagID: setpoint.tagID,
            setpointID: setpoint.id
        });
    }

    async function onDisableAll() {
        const hasUnacknowledgedAlarms = data?.alarmSetpoints.some(o => o.inAlarm && !o.alarmAcknowledged) ?? false;

        // Show confirmation dialog
        const result = await confirm({
            dialogTitle: t("alarmSetpointList.disableAllAlarmsConfirmation.title"),
            confirmationBodyComponent:
                <Stack direction="column" spacing={2}>
                    <Box>{t("alarmSetpointList.disableAllAlarmsConfirmation.confirmationText1")}</Box>
                    {hasUnacknowledgedAlarms &&
                        <Box>{t("alarmSetpointList.disableAllAlarmsConfirmation.confirmationText2")}</Box>
                    }
                    <Box>{t("alarmSetpointList.disableAllAlarmsConfirmation.confirmationText3")}</Box>
                </Stack>,
            yesButtonText: t("alarmSetpointList.disableAllAlarmsConfirmation.yesButtonText"),
            noButtonText: t("alarmSetpointList.disableAllAlarmsConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        if (result === "yes") {
            await disableDialog.open({
                targetObject: routeContext.contextObject as IeLynxObject
            });
        }
    }

    async function onEnableAll() {
        // Show confirmation dialog
        const result = await confirm({
            dialogTitle: t("alarmSetpointList.enableAllAlarmsConfirmation.title"),
            confirmationBodyComponent:
                <Stack direction="column" spacing={2}>
                    <Box>{t("alarmSetpointList.enableAllAlarmsConfirmation.confirmationText1")}</Box>
                    <Box>{t("alarmSetpointList.enableAllAlarmsConfirmation.confirmationText2")}</Box>
                </Stack>,
            yesButtonText: t("alarmSetpointList.enableAllAlarmsConfirmation.yesButtonText"),
            noButtonText: t("alarmSetpointList.enableAllAlarmsConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        if (result === "yes") {
            enableObjectSetPoints();
        }
    }

    async function onEdit(setpoint: AlarmSetpoint) {

        return _onEdit(setpoint.id, setpoint.tagName);
    }

    async function onUnlink(setpoint: AlarmSetpoint) {

        const result = await confirm({
            dialogTitle: t("setpointAdmin.unlink.title"),
            confirmationText: t("setpointAdmin.unlink.confirmation"),
            yesButtonText: t("setpointAdmin.delete.unlinkTag"),
            noButtonText: t("setpointAdmin.delete.doNotUnlinkTag"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        if (result === 'yes') {
            unlinkTag({
                setpointID: setpoint.id,
                tagID: setpoint.tagID
            });
        }
    }

    async function onTest(setpoint: AlarmSetpoint) {

        const result = await confirm({
            dialogTitle: t("setpointAdmin.test.title"),
            confirmationText: t("setpointAdmin.test.text"),
            yesButtonText: t("buttons.yes"),
            noButtonText: t("buttons.no"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        if (result === "yes") {

            try {
                await SetpointAdministrationDataService.testSetpoint(routeContext?.groupID as number, setpoint.id, setpoint.tagID);

                acknowledge(t('setpointAdmin.test.success', { name: setpoint.setPointName }));

            } catch (error) {
                const restError = error as RestError;

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }
        }
    }

    async function onRenameSetpoint(setpoint: AlarmSetpoint) {

        const result = await renameConfigurationDialog.open({
            configurationName: setpoint.setPointName,
            configurationType: t("objectTypeNames.setpoint"),
        });

        if (result.reason === "ok" && result.values) {
            try {
                const entity = await SetpointAdministrationDataService.getSetpoint(routeContext?.groupID as number, group!.organizationID!, setpoint.id);
                entity.setPointName = result.values.configurationName;
                saveSetpoint(entity);
            } catch (error) {
                const restError = error as RestError;
                
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }
        }
    }
    

    function renderItem(_index: number, item: AlarmSetpoint) {
        return (
            <AlarmSetpointListItem
                item={item}
                key={`${item.id}`}
                onAcknowledge={onAcknowledge}
                onDisable={onDisable}
                onEdit={onEdit}
                onTest={onTest}
                onUnlinkTag={onUnlink}
                onRename={onRenameSetpoint}
            />
        );
    };

    function renderGroupItem(_index: number, _groupKey: number, item: AlarmSetpoint) {
        return (
            <AlarmSetpointListItem
                item={item}
                key={`${item.id}`}
                onAcknowledge={onAcknowledge}
                onDisable={onDisable}
                onEdit={onEdit}
                onTest={onTest}
                onUnlinkTag={onUnlink}
                onRename={onRenameSetpoint}
            />
        );
    };

    function renderGroup(groupKey: number) {
        const groupText =
            groupKey === AlarmState.InAlarm
                ? t("alarmSetpointList.stateCategory.inAlarm")
                : groupKey === AlarmState.Acknowledged
                    ? t("alarmSetpointList.stateCategory.acknowledged")
                    : groupKey === AlarmState.Disabled
                        ? t("alarmSetpointList.stateCategory.disabled")
                        : groupKey === AlarmState.NotInAlarm
                            ? t("alarmSetpointList.stateCategory.notInAlarm")
                            : t("alarmSetpointList.stateCategory.unknown");

        return (
            <Box sx={{
                backgroundColor: "custom.background.1",
                height: theme => theme.spacing(6),
                width: 1,
                paddingLeft: 1,
                display: "flex",
                alignItems: "center"
            }}>
                {groupText}
            </Box>
        );
    }

    const noDataMessage = data?.totalCount === 0
        ? t("noData.noDataTypesForObject", { dataTypeName: t("dataTypeNames.alarmSetpoint"), objectName })
        : data?.filteredCount === 0
            ? t("noData.noFilteredDataTypesForObject", { dataTypeName: t("dataTypeNames.alarmSetpoint"), objectName })
            : undefined;

    return (
        <>
            {visible &&
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden"
                }}>
                    <LoadingZone isLoading={status === "loading"}>
                        <ContentContainer>
                            <ContentSection>
                                {noDataMessage &&
                                    <NoData text={noDataMessage} />
                                }

                                {/* Not grouped by alarm state */}
                                {!state.groupByAlarmState &&
                                    <VirtualizedList
                                        items={orderedData}
                                        renderItem={renderItem}
                                    />
                                }

                                {/* Grouped by alarm state */}
                                {state.groupByAlarmState &&
                                    <GroupedVirtualizedList
                                        items={orderedData}
                                        renderItem={renderGroupItem}
                                        renderGroup={renderGroup}
                                    />
                                }
                            </ContentSection>

                            <OptionsSideSheet onReset={() => resetOptions()}>
                                <SideSheetFilterInput
                                    label={t("alarmSetpointList.options.filterText")}
                                    value={state.filterText}
                                    onChange={(value) => {
                                        setFilterText(value);
                                    }}
                                />

                                <SideSheetSwitch
                                    label={t("alarmSetpointList.options.groupByAlarmState")}
                                    checked={state.groupByAlarmState}
                                    onChange={(checked) => {
                                        setGroupByAlarmState(checked);
                                    }}
                                />
                            </OptionsSideSheet>
                        </ContentContainer>
                    </LoadingZone>

                    <AlarmSetpointDisableDialog {...bindAlarmSetpointDisableDialog(disableDialog)} />
                </Box>
            }

            <AlarmSetpointEditorDialog {...bindAlarmSetpointEditorDialog(setpointEditorDialog)} />
            <LinkExistingSetpointDialog {...bindLinkExistingSetpointDialog(linkExistingSetpointDialog)} />
            <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} />            
        </>
    );
}

export default forwardRef(AlarmSetpointList);
